import { useEffect, useRef, useState } from 'react';
import { CARD_ROUTES } from '../historyMock';
import { dismissCardPayer, getCardDismissalsSync, getCardPayersSync } from '../store';
import { shortDate } from '../dates';
import { money } from '../utils';
import type { CardPayer } from '../types';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function monthOf(iso: string): string {
  return MONTH_NAMES[Number(iso.slice(5, 7)) - 1] ?? '';
}
function times(n: number): string {
  return n === 1 ? 'once' : `${String(n)} times`;
}

/**
 * "Switch to free EFT" (rcm-32 §6, D195): at the top of Posted, one card per
 * payer that paid by card in the last 90 days. "How to switch" shows the
 * payer's opt-out route; "Done, I asked them" hides the card. If that payer
 * pays by card again more than 30 days later, the card comes back.
 */
export function CardSwitchCards() {
  const [route, setRoute] = useState<CardPayer | null>(null);
  const payers = getCardPayersSync();
  const dismissed = getCardDismissalsSync();
  if (payers.length === 0) return null;
  return (
    <section className="po-switch-cards" aria-label="Switch to free EFT">
      {payers.map((p) => {
        const asked = dismissed[p.payer];
        const again = p.afterAsk > 0 && asked;
        return (
          <div key={p.payer} className="po-switchcard">
            <div className="po-switchcard-body">
              <div className="po-eyebrow">Switch to free EFT</div>
              {again ? (
                <p className="po-switchcard-text">
                  <strong>{p.payer} still paid by card.</strong>{' '}
                  You asked them to switch on {shortDate(asked)}, and they paid by card {times(p.afterAsk)} since. Ask them again: payers must pay by EFT when you ask.
                </p>
              ) : (
                <p className="po-switchcard-text">
                  {p.payer} paid you by card {times(p.count)} since {monthOf(p.firstIso)} and kept {money(p.fees)} in fees. Ask them to pay by free EFT instead: payers must pay by EFT when you ask.
                </p>
              )}
            </div>
            <button type="button" className="po-btn po-btn-sm" onClick={() => { setRoute(p); }}>How to switch</button>
          </div>
        );
      })}
      {route ? <RouteDialog p={route} onClose={() => { setRoute(null); }} /> : null}
    </section>
  );
}

function RouteDialog({ p, onClose }: { p: CardPayer; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const r = CARD_ROUTES[p.payer];
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button')?.focus();
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onClose]);
  return (
    <div className="po-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} className="po-dialog" role="dialog" aria-modal="true" aria-label={`How to switch ${p.payer} to free EFT`}>
        <h2 className="po-dialog-title">How to switch {p.payer} to free EFT</h2>
        {r ? (
          <>
            <p className="po-dialog-body">{p.payer} sends its card payments through {r.vendor}. Call {r.vendor} and ask them to pay your office by EFT instead of by card.</p>
            <dl className="po-route">
              <dt>Vendor</dt><dd>{r.vendor}</dd>
              <dt>Phone</dt><dd className="num">{r.phone}</dd>
            </dl>
          </>
        ) : (
          <p className="po-dialog-body">We don&apos;t have this payer&apos;s route yet. You can ask the number on the back of a patient&apos;s card to pay your office by EFT instead of by card.</p>
        )}
        <div className="po-dialog-actions">
          <button type="button" className="po-btn po-btn-primary" onClick={() => { dismissCardPayer(p.payer); onClose(); }}>Done, I asked them</button>
          <button type="button" className="po-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
